
import { useEffect, useState, RefObject } from 'react';

interface ParallaxConfig {
  speed?: number; // Speed factor, positive values move in the opposite direction of scroll
  startOffset?: number; // Starting offset in pixels
  clamp?: boolean; // Whether to stop the effect when element is out of view
}

export const useParallax = (
  elementRef: RefObject<HTMLElement>,
  config: ParallaxConfig = {}
) => {
  const { speed = 0.2, startOffset = 0, clamp = false } = config;
  const [offset, setOffset] = useState(startOffset);

  useEffect(() => {
    const handleScroll = () => {
      if (!elementRef.current) return;

      const rect = elementRef.current.getBoundingClientRect();
      const elementTop = rect.top;
      const elementBottom = rect.bottom;
      const windowHeight = window.innerHeight;

      // Check if element is in viewport
      if (
        (clamp && (elementBottom < 0 || elementTop > windowHeight)) ||
        (!clamp && elementBottom < 0)
      ) {
        return;
      }

      // Calculate parallax offset based on element position
      const scrollPosition = window.scrollY;
      const newOffset = startOffset + scrollPosition * speed;
      setOffset(newOffset);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initialize position

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [elementRef, speed, startOffset, clamp]);

  return { transform: `translateY(${offset}px)` };
};
